import {
  FormEvent,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import css from "../../common-styling/Dialog.module.css";
import buttonCss from "../../common-styling/Button.module.css";
import { DialogHandle } from "./DialogTypes.ts";
import CloseIcon from "@mui/icons-material/Close";
import { Portal } from "@mui/material";

interface Props {
  title: string;
  action: string;
  buttonsInHeader?: boolean;
  children?: React.ReactNode;
  className?: string;
  onSubmit: (data: FormData) => Promise<boolean | void>;
  onError: (error: unknown) => string;
  onOpen?: () => void;
  onClose?: () => void;
}

const Dialog = forwardRef(function Dialog(
  props: Props,
  ref: React.ForwardedRef<DialogHandle>,
) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const handle = {
    show: () => {
      dialogRef.current?.showModal();
      props.onOpen?.();
    },
    hide: () => {
      dialogRef.current?.close();
      props.onClose?.();
    },
  };
  useImperativeHandle(ref, () => handle);
  function onSubmit(submitEvent: FormEvent<HTMLFormElement>) {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const form = dialog.querySelector("form")! as HTMLFormElement;
    setLoading(true);
    // Don't immediately hide dialog
    submitEvent.preventDefault();
    props
      .onSubmit(new FormData(form))
      .then((stayVisible) => {
        setLoading(false);
        setError(null);
        if (!stayVisible) {
          form.reset();
          handle.hide();
        }
      })
      .catch((err) => {
        setLoading(false);
        const errorMsg = props.onError(err);
        setError(errorMsg || "Failed to submit.");
      });
  }
  return (
    <Portal>
      <dialog
        className={[css.dialog, props.className].join(" ")}
        ref={dialogRef}
      >
        <div className={css["dialog-container"]}>
          <div className={css["dialog-header"]}>
            <h2>{props.title}</h2>
            <button
              className={css["close-button"]}
              onClick={handle.hide}
              aria-label="Close dialog"
            >
              <CloseIcon className={css["dialog-close-icon"]} />
            </button>
          </div>

          <form method="dialog" onSubmit={onSubmit}>
            {error ? <div className={css.error}>{error}</div> : ""}
            <div className={css.body}>{props.children}</div>
            <div
              className={
                css.buttons +
                (props.buttonsInHeader ? " " + css["in-header"] : "")
              }
            >
              <button
                value="submit"
                className={buttonCss["action-button"]}
                aria-label="Submit"
              >
                {props.action}
              </button>
            </div>
          </form>
        </div>
        {loading ? <div className={css.overlay} /> : ""}
      </dialog>
    </Portal>
  );
});

export default Dialog;
